import {
  ClockCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Typography } from 'antd';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { marketplaceApi } from '../api/marketplaceApi';
import { MarketplaceItemStatus } from '../types/types';

const { Text } = Typography;

interface MarketplaceTableActionsProps {
  itemId: number;
  itemName: string;
  status: MarketplaceItemStatus;
  onActionComplete: () => void;
}

export const MarketplaceTableActions: FC<MarketplaceTableActionsProps> = ({
  itemId,
  itemName,
  status,
  onActionComplete,
}) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/marketplace/create-or-update/${itemId}`);
  };

  const handleDeactivate = async () => {
    try {
      await marketplaceApi.deactivateItem(itemId);
      onActionComplete();
    } catch {
      Modal.error({
        title: 'Erro',
        content: 'Não foi possível desativar o produto. Tente novamente.',
        centered: true,
        okText: 'OK',
      });
    }
  };

  const handleActivate = async () => {
    try {
      await marketplaceApi.activateItem(itemId);
      onActionComplete();
    } catch {
      Modal.error({
        title: 'Erro',
        content: 'Não foi possível ativar o produto. Tente novamente.',
        centered: true,
        okText: 'OK',
      });
    }
  };

  const handleCancelSchedule = async () => {
    try {
      await marketplaceApi.cancelSchedule(itemId);
      onActionComplete();
    } catch {
      Modal.error({
        title: 'Erro',
        content: 'Não foi possível cancelar o agendamento. Tente novamente.',
        centered: true,
        okText: 'OK',
      });
    }
  };

  const confirmDeactivate = () => {
    Modal.confirm({
      title: 'Desativar produto?',
      icon: <ExclamationCircleOutlined />,
      centered: true,
      width: 650,
      okText: 'Desativar Item',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      content: (
        <Text>
          O produto {itemName} será removido do marketplace e não ficará mais
          visível para os usuários. Esta ação não afeta compras ou assinaturas
          já realizadas. Você poderá reativá-lo a qualquer momento.
        </Text>
      ),
      onOk: handleDeactivate,
    });
  };

  const confirmCancelSchedule = () => {
    Modal.confirm({
      title: 'Cancelar Agendamento?',
      icon: <ExclamationCircleOutlined />,
      centered: true,
      width: 650,
      okText: 'Cancelar publicação do agendamento',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      content: (
        <Text>
          O agendamento do produto {itemName} será cancelada e retornará para o
          status de rascunho.
        </Text>
      ),
      onOk: handleCancelSchedule,
    });
  };

  const confirmActivate = () => {
    Modal.confirm({
      title: 'Ativar produto no marketplace?',
      icon: <ExclamationCircleOutlined />,
      centered: true,
      width: 650,
      okText: 'Confirmar Ativação',
      cancelText: 'Cancelar',
      content: (
        <Text>
          O produto {itemName} voltará a ficar visível no marketplace para os
          usuários. Todas as configurações e ofertas serão mantidas.
        </Text>
      ),
      onOk: handleActivate,
    });
  };

  const handleClick: MenuProps['onClick'] = event => {
    switch (event.key) {
      case 'EDIT':
        handleEdit();
        break;
      case 'DEACTIVATE':
        confirmDeactivate();
        break;
      case 'ACTIVATE':
        confirmActivate();
        break;
      case 'CANCEL_SCHEDULE':
        confirmCancelSchedule();
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'EDIT',
      label: 'Editar',
      icon: <EditOutlined />,
    },
    status === MarketplaceItemStatus.Active
      ? {
          key: 'DEACTIVATE',
          label: 'Desativar',
          icon: <EyeInvisibleOutlined />,
        }
      : null,
    status === MarketplaceItemStatus.Scheduled
      ? {
          key: 'CANCEL_SCHEDULE',
          label: 'Cancelar Agendamento',
          icon: <ClockCircleOutlined />,
        }
      : null,
    status !== MarketplaceItemStatus.Active &&
    status !== MarketplaceItemStatus.Scheduled
      ? {
          key: 'ACTIVATE',
          label: 'Ativar',
          icon: <EyeOutlined />,
        }
      : null,
  ].filter(Boolean);

  return (
    <Dropdown
      placement='bottomRight'
      menu={{ items, onClick: handleClick }}
      trigger={['click']}
    >
      <ActionButton style={{ border: 'none' }}>
        <MoreOutlined />
      </ActionButton>
    </Dropdown>
  );
};

const ActionButton = styled(Button)`
  &:hover {
    background-color: rgba(62, 62, 62, 0.7);
    color: white;
  }
`;
